import React, { useState, useEffect } from "react";
import { Clock, Search, RefreshCw } from "lucide-react";
import axios from "../../api/axios";

const LoginLogoutHistoryTable = () => {
  const [historyData, setHistoryData] = useState([]);
  const [search, setSearch] = useState("");
  const [tenantKey, setTenantKey] = useState("ALL");
  const [role, setRole] = useState("ALL");
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = {};
      if (tenantKey !== "ALL") params.tenantKey = tenantKey;
      if (role !== "ALL") params.role = role;
      if (search.trim() !== "") params.search = search.trim();

      const res = await axios.get("/auth/history", { params });
      if (res.data.success) {
        setHistoryData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching login history:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchHistory();
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [search, tenantKey, role]);

  const getRoleBadge = (r) => {
    const roleLower = String(r || "").toLowerCase();
    if (roleLower === "admin") return "bg-purple-50 text-purple-700 border-purple-200";
    if (roleLower === "organization") return "bg-blue-50 text-blue-700 border-blue-200";
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  };

  return (
    <div className="p-2 bg-slate-100 min-h-screen text-xs">
      <div className="max-w-7xl mx-auto bg-white rounded shadow-2xs border border-slate-200 overflow-hidden">
        
        {/* --- HEADER BAR --- */}
        <div className="px-3 py-1.5 bg-[#0E3652] text-white flex justify-between items-center">
          <h2 className="font-bold flex items-center gap-1.5 text-xs">
            <Clock size={14} className="text-[#F4B429]" /> Login Activity History
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchHistory}
              className="px-2 py-0.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded text-[10px] font-semibold flex items-center gap-1 transition"
            >
              <RefreshCw size={10} className={loading ? "animate-spin" : ""} /> Sync
            </button>
            <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded text-[#F4B429] font-bold">
              Total: {historyData.length}
            </span>
          </div>
        </div>

        {/* --- FILTERS & SEARCH --- */}
        <div className="p-2 bg-slate-50 border-b border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none text-slate-400">
              <Search size={12} />
            </span>
            <input
              type="text"
              placeholder="Search ID, Name, Email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1 bg-white border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0E3652]"
            />
          </div>

          <select
            value={tenantKey}
            onChange={(e) => setTenantKey(e.target.value)}
            className="py-1 px-2 bg-white border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0E3652]"
          >
            <option value="ALL">All Tenants</option>
            <option value="MED001">MED001</option>
          </select>

          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="py-1 px-2 bg-white border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#0E3652]"
          >
            <option value="ALL">All Roles</option>
            <option value="admin">Admin</option>
            <option value="organization">Organization</option>
            <option value="student">Student</option>
          </select>
        </div>

        {/* --- ULTRA SMALL TABLE --- */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left">
            <thead className="bg-slate-100 text-slate-800 uppercase font-bold text-[10px]">
              <tr>
                <th className="px-2 py-1.5">User Name</th>
                <th className="px-2 py-1.5">User ID</th>
                <th className="px-2 py-1.5">Login Account</th>
                <th className="px-2 py-1.5">Role</th>
                <th className="px-2 py-1.5">Tenant</th>
                <th className="px-2 py-1.5">Date</th>
                <th className="px-2 py-1.5">Login Timestamps</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-slate-500 font-medium">Loading...</td>
                </tr>
              ) : historyData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-slate-500 font-medium">No records found.</td>
                </tr>
              ) : (
                historyData.map((row) => (
                  <tr key={row._id} className="hover:bg-slate-50 transition">
                    <td className="px-2 py-1 font-bold text-slate-900">{row.name}</td>
                    <td className="px-2 py-1 font-mono text-slate-700">{row.userId}</td>
                    <td className="px-2 py-1 text-blue-700 font-medium">{row.loginBy}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded border uppercase inline-block ${getRoleBadge(row.role)}`}>
                        {row.role}
                      </span>
                    </td>
                    <td className="px-2 py-1 font-mono text-slate-600">{row.tenantKey}</td>
                    <td className="px-2 py-1 font-semibold text-slate-700">{row.date}</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                        {row.sessions?.map((session, idx) => (
                          <span key={idx} className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-1 py-0.5 rounded text-[10px] font-mono font-bold flex items-center gap-0.5">
                            🟢 {session.loginTime}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default LoginLogoutHistoryTable;